import * as React from "react";

export type TagLabelType =
  | "normal"
  | "brand"
  | "success"
  | "warning"
  | "error"
  | "disabled"
  | "orange"
  | "lavender";

interface Props {
  children: React.ReactNode;
  type?: TagLabelType;
  /** error 系タグを枠線付きで強調する（公開ブロック等）。 */
  emphasis?: boolean;
}

// DESIGN.md §8.4 + §4.2.2/§4.2.3. The legacy `type` API is preserved (admin
// code throughout passes type="success"|"warning"|"error"|"disabled"|"normal");
// internally we map each type to a feedback (or surface-subtle for neutral,
// brand-soft for the brand-flavored chip) pair so the colors stay consistent
// with the rest of the platform.
const TYPE_CLASS: Record<TagLabelType, string> = {
  normal: "bg-surface-subtle text-text-secondary",
  brand: "bg-surface-brand-soft text-text-brand",
  success: "bg-feedback-success-bg text-feedback-success-fg",
  warning: "bg-feedback-warning-bg text-feedback-warning-fg",
  error: "bg-feedback-danger-bg text-feedback-danger-fg",
  disabled: "bg-surface-subtle text-text-disabled",
  orange: "bg-tag-orange-100 text-tag-orange-700",
  lavender: "bg-tag-lavender-100 text-tag-lavender-700",
};

const EMPHASIS_CLASS: Partial<Record<NonNullable<Props["type"]>, string>> = {
  error: "font-semibold ring-1 ring-inset ring-feedback-danger-border",
};

const BASE_CLASS =
  "inline-flex items-center w-fit px-2.5 h-6 rounded-full text-xs font-medium leading-snug";

export const TagLabel = React.memo(
  ({ children, type = "normal", emphasis = false, ...props }: Props) => {
    const emphasisClass = emphasis ? EMPHASIS_CLASS[type] : undefined;
    return (
      <span
        className={[BASE_CLASS, TYPE_CLASS[type], emphasisClass].filter(Boolean).join(" ")}
        {...props}
      >
        {children}
      </span>
    );
  },
);
TagLabel.displayName = "TagLabel";
